import React, { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import { Loader2, PoundSterling, TrendingUp, TrendingDown, Clock } from "lucide-react";
import {
    ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

function StatCard({ label, value, sub, tone = "ink", testid }) {
    const toneCls = {
        ink: "text-ink",
        red: "text-stamp-red",
        green: "text-stamp-green",
        blue: "text-stamp-blue",
    }[tone];
    return (
        <div className="paper-card p-5" data-testid={testid}>
            <div className="label-typed tw-prefix mb-1">{label}</div>
            <div className={`font-heading text-3xl tracking-[0.03em] ${toneCls}`}>{value}</div>
            {sub && <div className="label-typed mt-1">{sub}</div>}
        </div>
    );
}

const fmt = (n) => {
    if (n == null || Number.isNaN(n)) return "—";
    const abs = Math.abs(n);
    if (abs >= 1000) return `£${(n / 1000).toFixed(1)}k`;
    return `£${n.toFixed(2)}`;
};

export default function Cost() {
    const [data, setData] = useState(null);
    const [weeks, setWeeks] = useState(8);
    const [err, setErr] = useState("");

    useEffect(() => {
        setData(null);
        api.get("/shed/cost-dashboard", { params: { weeks } })
            .then((r) => setData(r.data))
            .catch((e) => setErr(e.response?.data?.detail?.toString() || "Failed"));
    }, [weeks]);

    if (err) return <Shell><div className="text-stamp-red label-typed">// {err}</div></Shell>;
    if (!data) return <Shell><div className="flex items-center gap-2 text-ink-soft label-typed"><Loader2 className="w-4 h-4 animate-spin" /> Running the numbers...</div></Shell>;

    const t = data.totals;
    const r = data.runway;
    const series = data.weekly.series;

    return (
        <Shell>
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-typed tw-prefix">Finance · The Money to Run the Shed</div>
                    <h1 className="font-heading text-5xl uppercase tracking-[0.04em] text-ink mt-1 flex items-center gap-3">
                        <PoundSterling className="w-10 h-10 text-stamp-red" strokeWidth={1.5} />
                        Cost<span className="cursor-blink" />
                    </h1>
                    <p className="font-body text-sm text-ink-soft mt-3 max-w-2xl">
                        Direct (AI credits, hosting). Indirect (time, tools). Income (Shop, consulting).
                        Runway at current pace. {data.entry_count} finance entr{data.entry_count === 1 ? "y" : "ies"} in the file.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <span className="label-typed">window:</span>
                    {[4, 8, 13, 26].map((n) => (
                        <button
                            key={n}
                            onClick={() => setWeeks(n)}
                            className={`font-heading uppercase text-[11px] tracking-[0.22em] px-3 py-2 border ${weeks === n ? "border-stamp-red text-stamp-red bg-paper-dark/50" : "border-ink/25 text-ink-soft hover:border-stamp-red hover:text-stamp-red"}`}
                            data-testid={`cost-window-${n}`}
                        >
                            {n} wk
                        </button>
                    ))}
                </div>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                <StatCard testid="stat-direct" label="Direct cost" value={fmt(t.direct)} sub="AI / hosting / live spend" tone="red" />
                <StatCard testid="stat-indirect" label="Indirect cost" value={fmt(t.indirect)} sub="time / tools attributable" tone="red" />
                <StatCard testid="stat-other" label="Other out" value={fmt(t.other_out)} sub="uncategorised spend" tone="ink" />
                <StatCard testid="stat-income" label="Income" value={fmt(t.income)} sub="Shop / consulting / misc" tone="green" />
                <StatCard
                    testid="stat-net"
                    label="Net"
                    value={fmt(t.net)}
                    sub={t.net >= 0 ? "surplus" : "shortfall"}
                    tone={t.net >= 0 ? "green" : "red"}
                />
            </div>

            {/* Runway card */}
            <div className="paper-card tape-corner p-5 mb-6 tilt-l" data-testid="runway-card">
                <div className="flex items-center gap-3 mb-3 label-typed tw-prefix">
                    <Clock className="w-3 h-3" /> Runway · pace over last {data.window_weeks} weeks
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 font-body text-sm">
                    <div>
                        <div className="label-typed">Weekly burn</div>
                        <div className="font-heading text-xl text-stamp-red">{fmt(r.weekly_burn)}</div>
                    </div>
                    <div>
                        <div className="label-typed">Weekly income</div>
                        <div className="font-heading text-xl text-stamp-green">{fmt(r.weekly_income)}</div>
                    </div>
                    <div>
                        <div className="label-typed">Weekly gap</div>
                        <div className={`font-heading text-xl ${r.weekly_gap > 0 ? "text-stamp-red" : "text-stamp-green"}`}>
                            {r.weekly_gap > 0 ? <TrendingDown className="w-4 h-4 inline mr-1" /> : <TrendingUp className="w-4 h-4 inline mr-1" />}
                            {fmt(r.weekly_gap)}
                        </div>
                    </div>
                    <div>
                        <div className="label-typed">Runway at current pace</div>
                        <div className="font-heading text-xl text-ink">
                            {r.runway_weeks_at_current_income == null
                                ? (r.weekly_gap <= 0 ? "Indefinite — income covers pace" : "Unknown — income = 0")
                                : `${r.runway_weeks_at_current_income} wk`}
                        </div>
                    </div>
                </div>
            </div>

            {/* Weekly chart */}
            <div className="paper-card p-5 mb-6" data-testid="cost-weekly-chart">
                <div className="label-typed tw-prefix mb-3">Weekly breakdown · cost vs income</div>
                {series.every(s => !s.direct && !s.indirect && !s.income && !s.other_out) ? (
                    <div className="text-center py-12 font-body text-sm text-ink-faint">
                        No finance entries in this window. Shred some receipts / invoices / bills through the Shredder.
                    </div>
                ) : (
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={series}>
                                <CartesianGrid strokeDasharray="2 4" stroke="#4A443C" />
                                <XAxis dataKey="week" stroke="#4A443C" tick={{ fontSize: 10 }} />
                                <YAxis stroke="#4A443C" tick={{ fontSize: 10 }} />
                                <Tooltip
                                    contentStyle={{ background: "#F4ECD8", border: "1px solid #4A443C", color: "#1A1714", fontFamily: "Courier Prime" }}
                                    formatter={(v) => fmt(v)}
                                />
                                <Legend wrapperStyle={{ fontFamily: "Courier Prime", fontSize: 10 }} />
                                <Bar stackId="cost" dataKey="direct" fill="#B22020" name="Direct" />
                                <Bar stackId="cost" dataKey="indirect" fill="#8B6914" name="Indirect" />
                                <Bar stackId="cost" dataKey="other_out" fill="#7A6F5F" name="Other out" />
                                <Line type="monotone" dataKey="income" stroke="#3B6E3B" strokeWidth={2} name="Income" />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>

            {/* Top vendors */}
            <div className="paper-card p-5" data-testid="top-vendors">
                <div className="label-typed tw-prefix mb-3">Top vendors (money out)</div>
                {data.top_vendors.length === 0 ? (
                    <div className="font-body text-sm text-ink-faint italic">No outgoing spend filed yet.</div>
                ) : (
                    <ul className="divide-y divide-ink/15">
                        {data.top_vendors.map((v, i) => (
                            <li key={v.vendor} className="flex items-center justify-between py-2">
                                <span className="font-body text-sm text-ink">
                                    <span className="label-typed inline-block w-6">{i + 1}.</span>
                                    {v.vendor}
                                </span>
                                <span className="font-heading text-base text-stamp-red">{fmt(v.amount)}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </Shell>
    );
}
